"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
const navItems = [{
  id: "inicio",
  label: "Inicio",
  href: "/"
}, {
  id: "servicios",
  label: "Servicios",
  href: "/servicios/"
}, {
  id: "sectores",
  label: "Sectores",
  href: "/#sectores-de-especialidad"
}, {
  id: "proyectos",
  label: "Proyectos",
  href: "/#proyectos"
}, {
  id: "capacidades",
  label: "Capacidades",
  href: "/capacidades/"
}, {
  id: "contacto",
  label: "Contacto",
  href: "/#contacto"
}, {
  id: "dossier-corporativo",
  label: "Dossier Corporativo",
  href: "https://mivgrupo.com/wp-content/uploads/2026/04/dossierMIV-light.pdf",
  target: "_blank"
}];
function DesktopNav() {
  const {
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6 items-center">
        {[1, 2, 3, 4, 5, 6].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-foreground/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-8">
        {navItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
        
  <Link to={item.href} target={item.id === "dossier-corporativo" ? "_blank" : "_self"} rel="noopener noreferrer" className="group relative py-2 text-[14px] font-mono text-foreground transition-colors hover:text-primary">
                 {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-none md:hidden overflow-hidden animate-in slide-in-from-top-2">
      <nav className="flex flex-col px-4 py-6 space-y-4">
        {loading ? [1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-foreground/10 animate-pulse rounded-sm" />) : navItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href} className="text-lg font-mono text-foreground py-2 border-b border-border hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
              {item.label}
            </Link>)}
        <div className="pt-4">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-none active:translate-y-[1px] transition-all duration-200 h-11 text-[14px] font-mono rounded-sm" onClick={() => setIsOpen(false)} asChild>
            <Link to="#contacto">Solicitar Cotización</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerHeight = 80;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerHeight;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="navegación-principal" className="sticky top-0 z-50 w-full transition-all duration-300 border-b dark bg-background border-border !text-[hsl(226,71%,40%)]">
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-6 flex items-center justify-between gap-6">
        <div className="flex items-center justify-between w-full gap-8">
          <Link to="/" className="flex items-center z-10 shrink-0 h-10 md:h-20">
            <img src="https://mivgrupo.com/wp-content/uploads/2026/04/logo-miv-mas-ultimo.jpg" alt="MIV Grupo - Mecanizados Industriales de Venezuela" className="object-contain brightness-110 contrast-125 !w-[160px] !h-[65px] md:!w-[280px] md:!h-[115px] !rounded-xl" />
          </Link>

          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="26">
              <DesktopNav />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-4 z-10 shrink-0">
            <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 shadow-none active:translate-y-[1px] transition-all duration-200 h-10 px-6 text-[14px] font-mono rounded-sm" asChild>
              
            </Button>
            
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Alternar menú">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      <MenuProvider menu_id="26">
        <MobileNav isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}